import React, { useEffect, useRef, useState } from "react";
import { GeneralProperties } from "./general-properties/general-properties";
import Reminders from "./reminders/reminders";
import Workflow from "./workflow/workflow";
import { useHistory } from "react-router";
import { NIL as NIL_UUID } from "uuid";
import {
  loadDurationFields,
  reminderHelpers,
} from "./reminders/reminder-helpers";
import CancelModal from "../../../../../components/cancel-modal/cancel-modal";
import TaskTypesCreationModal from "../task-types-creation/components/task-types-creation-modal";
import {
  CreateTaskTypeAPI,
  EditTaskTypeAPI,
} from "../../../../../services/api/task-types-api";
import { PulseLoader } from "react-spinners";
import { FluentTabList } from "../../../../../components/FluentUI/Fluent-TabList";
import { PrepareBreadCrumbList, tabList } from "./task-type-util";
import FluentBreadcrumb from "../../../../../components/FluentUI/Fluent-Breadcrumb";
import { FluentButton } from "../../../../../components/FluentUI/Fluent-Button";
import FluentUiIcon from "../../../../../assets/fluent-ui-icon";
import "./task-type-new.scss";
import { FormDesign } from "./form-design/form-design";
import { TaskLayout } from "./task-layout/task-layout";
import { FormAutomation } from "./form-automation/forms/form-automation";
import { FormAutomationEnhanced } from "./form-automation-enhanced/forms/form-automation-enhanced";
import FormStatuses from "./form-statuses/form-statuses";

export const TaskTypeNew = () => {
  const history = useHistory();
  const taskTypeId = history.location.pathname.split("/").pop() ?? 0;
  const editMode = taskTypeId > 0;
  const dynamicForms = useRef(null);
  const [formAutomation, setFormAutomation] = useState([]);
  const [formStatuses, setFormStatuses] = useState(null);
  const taskLayout = useRef(null);

  const [selectedValue, setSelectedValue] = useState("General");
  const [showModal, setShowModal] = useState({
    cancelModel: false,
    successModal: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [enableCustomizedWorkflow, setEnableCustomizedWorkflow] =
    useState(false);
  const [customReminders, setCustomReminders] = useState([]);
  const [taskType, setTaskType] = useState(
    history.location.state?.taskType ?? {
      Id: history.location.pathname.split("/").pop() ?? 0,
      Type: "",
      IsPublic: true,
      Days: 2,
      WorkflowId: NIL_UUID,
      DefaultDurationUnits: 2,
      DefaultDurationInterval: "Days",
    }
  );
  const [taskReminder, setTaskReminder] = useState(
    reminderHelpers.emptyReminderObject()
  );

  const setFormData = (data, selectedFormId) => {
    setFormAutomation(prev => {
      const hasMatch = prev.some(row => row.FormId === selectedFormId);

      if (hasMatch) {
        // Replace all rows with matching FormId
        const filtered = prev.filter(row => row.FormId !== selectedFormId);
        return [...filtered, ...data];
      } else {
        // No match found, add all new data
        return [...prev, ...data];
      }
    });
  };

  useEffect(() => {
    if (editMode) {
      if (!taskType) return;
      if (taskType.Reminder) {
        setTaskReminder(reminderHelpers.getReminderDetailsFromTask(taskType));
      }
      if (taskType.CustomReminders) {
        taskType?.CustomReminders.map((cr) => {
          cr.ReminderTime = new Date(cr.ReminderTime);
          return cr;
        });
        setCustomReminders(taskType.CustomReminders);
      }
      if (taskType.WorkflowId != NIL_UUID) {
        setEnableCustomizedWorkflow(true);
      }
      loadDurationFields(
        taskType.Days,
        taskType.IsBusinessDays,
        taskType,
        setTaskType
      );
    }
  }, []);

  const handleSave = async () => {
    let deletedFormDesignFieldIds = dynamicForms?.current
      ? dynamicForms?.current?.DynamicFormFieldIdsToBeDeleted
      : [];
    let deletedTaskLayoutFieldIds = taskLayout?.current
      ? taskLayout?.current?.DynamicFormFieldIdsToBeDeleted
      : [];
    let FormAutomations = formAutomation?.map((fa) => {
      return (
        {
          DynamicFormAutomation: {
            Id: fa.Id || 0,
            AutomationOrder: fa.AutomationOrder,
            FormId: fa.FormId,
            TaskTypeId: taskType.Id,
          },
          DynamicFormAutomationWhen: fa.When,
          DynamicFormAutomationThen: fa.Then
        }
      )
    }) || [];

    let taskTypeRequest = {
      CreatorUserId: taskType?.CreatorUserId,
      CurrentUser: taskType?.CurrentUser,
      Id: taskType?.Id,
      IsOnDashboard: taskType?.IsOnDashboard,
      IsPublic: taskType?.IsPublic,
      IsVisible: taskType?.IsVisible,
      Type: taskType?.Type,
      Days: taskType?.Days,
      IsBusinessDays: taskType?.IsBusinessDays,
      WorkflowId: taskType?.WorkflowId,
      CustomReminders: customReminders,
      ...(taskReminder.exist && {
        reminder: taskReminder.reminder.name,
        ...reminderHelpers.getReminderDetails(taskReminder),
      }),
      DynamicFormIdsToBeDeleted:
        dynamicForms?.current?.DynamicFormIdsToBeDeleted,
      DynamicSubFormIdsToBeDeleted:
        dynamicForms?.current?.DynamicSubFormIdsToBeDeleted,
      DynamicFormFieldIdsToBeDeleted: [
        ...deletedFormDesignFieldIds,
        ...deletedTaskLayoutFieldIds,
      ],
      DynamicForm: dynamicForms?.current?.data,
      TaskLayoutDetail: taskLayout.current?.data?.TaskDetailsForm,
      TaskLayoutComment: taskLayout.current?.data?.TaskCommentsForm,
      FormAutomations: FormAutomations,
    };
    setIsLoading(true);

    const newTaskTypeResponse = editMode
      ? await EditTaskTypeAPI(taskTypeRequest)
      : await CreateTaskTypeAPI(taskTypeRequest);

    if (
      newTaskTypeResponse.status === 200 ||
      newTaskTypeResponse.status === 201
    ) {
      setIsLoading(false);
      setTaskType(newTaskTypeResponse.data);
      setShowModal((prev) => ({ ...prev, successModal: true }));
      loadDurationFields(
        newTaskTypeResponse.data.Days,
        newTaskTypeResponse.data.IsBusinessDays,
        newTaskTypeResponse.data,
        setTaskType
      );
    } else {
      console.log("Error [CreateTaskTypeAPI] => ", newTaskTypeResponse);
      setIsLoading(false);
    }
    setSelectedValue("General");
    dynamicForms.current = null;
    taskLayout.current = null;
    history.push({
      pathname: `/admin/task-types/task-type/${newTaskTypeResponse.data.Id}`,
      state: { taskType: newTaskTypeResponse.data },
    });
  };

  const SetDataRef = (data, type = "formDesign") => {
    switch (type) {
      case "formDesign":
        dynamicForms.current = data;
        break;
      case "taskLayout":
        taskLayout.current = data;
        break;
      default:
        break;
    }
  };

  const SelectedTabContent = () => {
    switch (selectedValue) {
      case "General":
        return (
          <GeneralProperties taskType={taskType} setTaskType={setTaskType} />
        );
      case "Reminders":
        return (
          <Reminders
            taskReminder={taskReminder}
            setTaskReminder={setTaskReminder}
            customReminders={customReminders}
            setCustomReminders={setCustomReminders}
          />
        );
      case "Form Statuses":
        return <FormStatuses taskTypeId={taskTypeId} onStatusesChange={setFormStatuses} />;
      case "Task Layout":
        return (
          <TaskLayout
            taskLayoutFromParent={taskLayout.current}
            taskTypeId={taskTypeId}
            SetTaskLayoutForm={SetDataRef}
          ></TaskLayout>
        );
      case "Form":
        return (
          <FormDesign
            formDataFromParent={dynamicForms.current}
            taskTypeId={taskTypeId}
            SetDynamicForms={SetDataRef}
          />
        );
      case "In-Form Automations":
        return <FormAutomation taskTypeId={taskTypeId} formDataFromParent={dynamicForms.current} />;
      case "In-Form Automations Enhanced":
        return <FormAutomationEnhanced
          formDataFromParent={dynamicForms.current}
          formStatusesFromParent={formStatuses}
          formAutomationFromParent={formAutomation}
          SetFormAutomation={setFormData}
          taskTypeId={taskTypeId}
          taskType={taskType} />;
      case "Workflow":
        return (
          <Workflow
            taskType={taskType}
            setTaskType={setTaskType}
            enableCustomizedWorkflow={enableCustomizedWorkflow}
            setEnableCustomizedWorkflow={setEnableCustomizedWorkflow}
          />
        );
      case "Rules":
        return <div>Rules is under construction</div>;
      default:
        return <div>General Properties</div>;
    }
  };

  return (
    <div className="task-type-container">
      {isLoading ? (
        <div className="pulse-loader">
          <PulseLoader></PulseLoader>
        </div>
      ) : (
        <>
          <div className="task-type-left-panel">
            <div
              className="task-type-left-panel-back"
              onClick={() => history.push("/admin?tab=task-types")}
            >
              <FluentUiIcon
                name={"ArrowLeftRegular"}
                color={"#0F6CBD"}
                size={20}
              />
              <span className="pl-2">Back to Task Types</span>
            </div>

            <FluentTabList
              tabList={tabList}
              tabClick={(selection) => setSelectedValue(selection)}
              currentTab={selectedValue}
            />
          </div>

          <div className="task-type-content">
            <div className="task-type-content-header">
              <FluentBreadcrumb
                breadcrumbs={PrepareBreadCrumbList(
                  taskType?.Type,
                  selectedValue,
                  setSelectedValue
                )}
              />
            </div>

            <div className="task-type-content-form">{SelectedTabContent()}</div>

            <div className="task-type-content-footer">
              <FluentButton
                onClick={() =>
                  setShowModal((prev) => ({ ...prev, cancelModel: true }))
                }
                text={"Cancel"}
                backgroundColor={"#0F6CBD"}
              />
              <div className="task-type-content-footer-button">
                <FluentButton
                  onClick={handleSave}
                  appearance="primary"
                  text={editMode ? "Save Task Type" : "Create Task Type"}
                  backgroundColor={"#0F6CBD"}
                // disabled={
                //   (enableCustomizedWorkflow &&
                //     taskType.WorkflowId == NIL_UUID) ||
                //   taskType.Type == ""
                // }
                />
              </div>
            </div>
          </div>
        </>
      )}

      {showModal.cancelModel && (
        <CancelModal
          show={showModal.cancelModel}
          onHide={() =>
            setShowModal((prev) => ({ ...prev, cancelModel: false }))
          }
          redirectURL={"/admin?tab=task-types"}
        />
      )}
      {showModal.successModal && (
        <TaskTypesCreationModal
          show={showModal.successModal}
          editMode={editMode}
          hideModal={() =>
            setShowModal((prev) => ({ ...prev, successModal: false }))
          }
        />
      )}
    </div>
  );
};
